import camelize from 'camelize';
import isEmpty from 'lodash/isEmpty';

import {
  BattlecardActions,
  BookmarkActions,
  CoachingActions,
  CopilotActions,
  HighlightActions,
  MeetingActions,
  MeetingTranslationActions,
  RealtimeAssistantActions,
  RealtimeTranscriptFetchActions,
  ScorecardsActions,
  WebsocketActions
} from 'actions/actionTypes';
import { readCopilotOmnisearchRequestIdFromWsData } from 'actions/copilotOmnisearch';
import { trackSegmentEvents } from 'actions/segment';

import {
  getCopilotStreamDataJsonContextUuids,
  sanitizeCopilotStreamMentions
} from 'helpers/copilot';
import {
  buildCopilotRunEventsUrl,
  connectCopilotRun,
  createCopilotRunEventDeduper,
  getCopilotChunkWatchdogDelayMs,
  getCopilotRunUuid,
  getCopilotStreamChatUuid,
  getCopilotStreamMessageData,
  hasCopilotRunStreamProgress,
  isActiveCopilotRunStatus,
  loadStoredCopilotRuns,
  removeStoredCopilotRun,
  saveStoredCopilotRun,
  startCopilotRun
} from 'helpers/copilotStreamClient';
import env from 'helpers/env';
import { L10nCopilot } from 'helpers/l10n/L10nCopilot';
import logError from 'helpers/logError';

import { COPILOT_ERROR_MESSAGE, MESSAGE_AUTHOR_TYPES } from 'constants/copilot';

const COPILOT_SSE_STREAM_FLAG = 'copilot_sse_stream';
const HTTP_CONFLICT_ERROR_NAME = 'Conflict';

const getCopilotMessageDataJsonForAction = action => {
  const contextPayload =
    getCopilotStreamDataJsonContextUuids(
      action.contextType,
      action.contextUuid,
      action.additionalContextMentions,
      action.primaryContextLabel
    ) || {};

  return {
    ...contextPayload,
    ...(contextPayload.mentions
      ? { mentions: sanitizeCopilotStreamMentions(contextPayload.mentions) }
      : {}),
    ...(action.userPromptUuid && {
      user_prompt_uuid: action.userPromptUuid
    }),
    ...(action.webSearchEnabled ? { tools: ['search_web'] } : {}),
    ...(action.attachmentUuids?.length
      ? { attachment_uuids: action.attachmentUuids }
      : {})
  };
};

const getMessageForAction = (store, action, wsAction) => {
  if (
    action.streamType === WebsocketActions.TYPE_COPILOT &&
    wsAction === 'send'
  ) {
    const contextPayload =
      getCopilotStreamDataJsonContextUuids(
        action.contextType,
        action.contextUuid,
        action.additionalContextMentions,
        action.primaryContextLabel,
        action.primaryContextWireExtras
      ) || {};
    const dataJson = {
      ...contextPayload,
      ...(contextPayload.mentions
        ? { mentions: sanitizeCopilotStreamMentions(contextPayload.mentions) }
        : {}),
      ...(action.userPromptUuid && {
        user_prompt_uuid: action.userPromptUuid
      }),
      ...(action.webSearchEnabled ? { tools: ['search_web'] } : {}),
      ...(action.attachmentUuids?.length
        ? { attachment_uuids: action.attachmentUuids }
        : {})
    };
    return JSON.stringify({
      msg: 'stream',
      stream: action.streamType,
      user_email: store.getState().user.email,
      db_object: {
        uuid: action.chatUuid,
        entity_key: action.entityKey
      },
      data: {
        message: action.message,
        data_json: dataJson
      }
    });
  }

  return JSON.stringify({
    msg: 'stream',
    stream: action.streamType,
    action: wsAction,
    db_object: {
      uuid: action.uuid,
      entity_key: action.entityKey
    },
    meta: {
      ts: new Date().toISOString(),
      user_email: store.getState().user.email
    }
  });
};

const dispatchOptimisticCopilotUserMessage = (store, messageAction) => {
  store.dispatch({
    type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
    data: {
      uuid: null,
      author: 1,
      message: messageAction.message,
      chatUuid: messageAction.chatUuid,
      created: messageAction.clientCreatedAt || new Date().toISOString(),
      ...(messageAction.copilotStandaloneContextPills?.length
        ? {
            copilotStandaloneContextPills:
              messageAction.copilotStandaloneContextPills
          }
        : {}),
      ...(messageAction.attachments?.length
        ? { attachments: messageAction.attachments }
        : {})
    }
  });
};

const getOmnisearchSocketPayload = (store, action) => {
  const data = {
    query: action.query,
    request_id: action.requestId
  };
  if (action.searchType) {
    data.search_type = action.searchType;
  }
  return JSON.stringify({
    msg: 'stream',
    stream: WebsocketActions.TYPE_COPILOT,
    user_email: store.getState().user.email,
    message_type: 'omnisearch',
    data
  });
};

const socketMiddleware = () => {
  let socket = null;
  let connecting = false;
  let pending = null;
  let processingSendMessage = false;
  let pendingCopilotMessages = [];
  /** Queued when user triggers omnisearch while WS is not open (e.g. global Copilot before any chat send). */
  let pendingOmnisearchAction = null;
  const durableCopilotConnectionsByRunUuid = {};
  const durableCopilotWatchdogsByRunUuid = {};
  const finishedDurableCopilotRunUuids = new Set();
  const markDurableCopilotEventProcessed = createCopilotRunEventDeduper();

  const clearDurableCopilotWatchdog = runUuid => {
    const watchdogId = durableCopilotWatchdogsByRunUuid[runUuid];
    if (watchdogId) {
      clearTimeout(watchdogId);
      delete durableCopilotWatchdogsByRunUuid[runUuid];
    }
  };

  const finishDurableCopilotRun = (
    store,
    run,
    {
      status = 'failed',
      errorMessage,
      showSystemMessage = true,
      dispatchTimeout = true
    } = {}
  ) => {
    if (!run?.runUuid || !run?.chatUuid) {
      return;
    }

    if (finishedDurableCopilotRunUuids.has(run.runUuid)) {
      return;
    }

    finishedDurableCopilotRunUuids.add(run.runUuid);
    clearDurableCopilotWatchdog(run.runUuid);
    durableCopilotConnectionsByRunUuid[run.runUuid]?.close();
    delete durableCopilotConnectionsByRunUuid[run.runUuid];
    removeStoredCopilotRun(run.chatUuid);

    if (showSystemMessage && errorMessage) {
      store.dispatch({
        type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
        data: {
          uuid: null,
          author: MESSAGE_AUTHOR_TYPES.SYSTEM,
          message: errorMessage,
          chatUuid: run.chatUuid,
          created: new Date().toISOString()
        }
      });
    }

    store.dispatch({
      type: CopilotActions.COPILOT_RUN_FINISHED,
      chatUuid: run.chatUuid,
      runUuid: run.runUuid,
      status
    });

    if (dispatchTimeout) {
      store.dispatch({ type: CopilotActions.MESSAGE_TIMEOUT });
    }
  };

  const startDurableCopilotChunkWatchdog = (store, run) => {
    if (!run?.runUuid || !run?.chatUuid) {
      return;
    }

    clearDurableCopilotWatchdog(run.runUuid);

    const delayMs = getCopilotChunkWatchdogDelayMs(run.startedAt);
    durableCopilotWatchdogsByRunUuid[run.runUuid] = setTimeout(() => {
      delete durableCopilotWatchdogsByRunUuid[run.runUuid];

      const activeRun =
        store.getState().copilot?.activeCopilotRunsByChatUuid?.[run.chatUuid];
      if (!activeRun || activeRun.runUuid !== run.runUuid) {
        return;
      }

      if (hasCopilotRunStreamProgress(activeRun)) {
        return;
      }

      finishDurableCopilotRun(store, run, {
        errorMessage: L10nCopilot.copilotNoReplyStartedTimeoutMessage
      });
    }, delayMs);
  };

  const pauseDurableCopilotSseForChat = (store, chatUuid) => {
    const run =
      store.getState().copilot?.activeCopilotRunsByChatUuid?.[chatUuid] ||
      loadStoredCopilotRuns()[chatUuid];

    if (!run?.runUuid) {
      return;
    }

    clearDurableCopilotWatchdog(run.runUuid);
    durableCopilotConnectionsByRunUuid[run.runUuid]?.close();
    delete durableCopilotConnectionsByRunUuid[run.runUuid];
  };

  const isCopilotSseStreamEnabled = store =>
    store.getState().user?.flags?.includes(COPILOT_SSE_STREAM_FLAG);

  const dispatchCopilotPayload = (store, payload, fallbackChatUuid) => {
    const normalizedPayload =
      payload && typeof payload === 'object' ? camelize(payload) : null;

    if (!normalizedPayload) {
      return;
    }

    const chatUuid = getCopilotStreamChatUuid(
      normalizedPayload,
      fallbackChatUuid
    );

    if (!chatUuid) {
      logError('Copilot stream payload missing chatUuid', {
        fallbackChatUuid,
        payloadMsg: normalizedPayload?.msg
      });
      return;
    }

    if (normalizedPayload?.msg === 'copilot_name_updated') {
      const nameData =
        normalizedPayload.data &&
        typeof normalizedPayload.data === 'object' &&
        !Array.isArray(normalizedPayload.data)
          ? camelize(normalizedPayload.data)
          : {};

      store.dispatch({
        type: CopilotActions.RECEIVE_COPILOT_NAME_UPDATE,
        data: {
          ...nameData,
          chatUuid
        }
      });
      return;
    }

    const messageData = getCopilotStreamMessageData(normalizedPayload);

    if (!messageData) {
      logError('Copilot stream payload missing message data', {
        chatUuid,
        payloadMsg: normalizedPayload?.msg
      });
      return;
    }

    store.dispatch({
      type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
      data: {
        ...messageData,
        chatUuid
      }
    });
  };

  const upsertDurableCopilotRun = (store, run) => {
    store.dispatch({
      type: CopilotActions.COPILOT_RUN_STARTED,
      run
    });
    saveStoredCopilotRun(run);
  };

  const connectDurableCopilotRun = (store, run) => {
    if (!run?.runUuid || !run?.chatUuid) {
      logError('Copilot SSE connect skipped: missing run or chat uuid', {
        chatUuid: run?.chatUuid,
        runUuid: run?.runUuid
      });
      return;
    }

    if (durableCopilotConnectionsByRunUuid[run.runUuid]) {
      startDurableCopilotChunkWatchdog(store, run);
      return run;
    }

    let currentRun = run;
    const eventsUrl = buildCopilotRunEventsUrl(
      run.runUuid,
      currentRun.lastEventId
    );

    durableCopilotConnectionsByRunUuid[run.runUuid] = connectCopilotRun(
      run.runUuid,
      {
        lastEventId: currentRun.lastEventId,
        onOpen: () => {
          finishedDurableCopilotRunUuids.delete(currentRun.runUuid);
          startDurableCopilotChunkWatchdog(store, currentRun);
        },
        onPayload: (payload, meta) => {
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          try {
            clearDurableCopilotWatchdog(currentRun.runUuid);

            const messageData = getCopilotStreamMessageData(payload);

            dispatchCopilotPayload(store, payload, currentRun.chatUuid);

            const updatedRun = {
              ...currentRun,
              lastEventId: meta.id || currentRun.lastEventId,
              assistantMessageUuid:
                (typeof messageData?.uuid === 'string' && messageData.uuid) ||
                currentRun.assistantMessageUuid,
              status: 'running'
            };
            currentRun = updatedRun;
            store.dispatch({
              type: CopilotActions.COPILOT_RUN_EVENT_RECEIVED,
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              lastEventId: updatedRun.lastEventId,
              assistantMessageUuid: updatedRun.assistantMessageUuid,
              status: updatedRun.status
            });
            saveStoredCopilotRun(updatedRun);
          } catch (error) {
            logError('Copilot SSE chunk handler failed', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              eventId: meta.id,
              errorMessage: error?.message
            });
          }
        },
        onCompleted: (payload, meta) => {
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          try {
            if (payload) {
              dispatchCopilotPayload(store, payload, currentRun.chatUuid);
            }
            finishDurableCopilotRun(store, currentRun, {
              status: 'completed',
              showSystemMessage: false,
              dispatchTimeout: true
            });
          } catch (error) {
            logError('Copilot SSE completed handler failed', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              eventId: meta.id,
              errorMessage: error?.message
            });
          }
        },
        onNameUpdated: (payload, meta) => {
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          try {
            if (payload) {
              dispatchCopilotPayload(store, payload, currentRun.chatUuid);
            }

            const updatedRun = {
              ...currentRun,
              lastEventId: meta.id || currentRun.lastEventId
            };
            currentRun = updatedRun;
            store.dispatch({
              type: CopilotActions.COPILOT_RUN_EVENT_RECEIVED,
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              lastEventId: updatedRun.lastEventId
            });
            saveStoredCopilotRun(updatedRun);
          } catch (error) {
            logError('Copilot SSE name_updated handler failed', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              eventId: meta.id,
              errorMessage: error?.message
            });
          }
        },
        onError: errorEvent => {
          const streamErrorMessage =
            (errorEvent &&
              typeof errorEvent === 'object' &&
              typeof errorEvent.message === 'string' &&
              errorEvent.message) ||
            COPILOT_ERROR_MESSAGE.message;

          logError('Copilot SSE stream error event', {
            chatUuid: currentRun.chatUuid,
            runUuid: currentRun.runUuid,
            eventsUrl,
            streamErrorMessage,
            hasPayload: Boolean(
              errorEvent && typeof errorEvent === 'object' && errorEvent.payload
            )
          });

          if (
            errorEvent &&
            typeof errorEvent === 'object' &&
            errorEvent.payload
          ) {
            try {
              dispatchCopilotPayload(
                store,
                errorEvent.payload,
                currentRun.chatUuid
              );
            } catch (error) {
              logError('Copilot SSE error payload dispatch failed', {
                chatUuid: currentRun.chatUuid,
                runUuid: currentRun.runUuid,
                errorMessage: error?.message
              });
            }
          }

          finishDurableCopilotRun(store, currentRun, {
            errorMessage: streamErrorMessage
          });
        },
        onNetworkError: event => {
          logError('Copilot SSE network error', {
            chatUuid: currentRun.chatUuid,
            runUuid: currentRun.runUuid,
            eventsUrl,
            readyState: event?.target?.readyState
          });

          if (event?.target?.readyState === EventSource.CLOSED) {
            finishDurableCopilotRun(store, currentRun, {
              errorMessage: L10nCopilot.connectionLostErrorMessage
            });
          }
        },
        onClose: () => {
          delete durableCopilotConnectionsByRunUuid[currentRun.runUuid];

          const activeRun =
            store.getState().copilot?.activeCopilotRunsByChatUuid?.[
              currentRun.chatUuid
            ];
          if (
            activeRun?.runUuid === currentRun.runUuid &&
            isActiveCopilotRunStatus(activeRun.status) &&
            !finishedDurableCopilotRunUuids.has(currentRun.runUuid)
          ) {
            finishDurableCopilotRun(store, currentRun, {
              showSystemMessage: false,
              dispatchTimeout: true
            });
          }
        }
      }
    );

    return run;
  };

  const beginDurableCopilotRun = (store, run) => {
    finishedDurableCopilotRunUuids.delete(run.runUuid);
    upsertDurableCopilotRun(store, run);
    connectDurableCopilotRun(store, run);
    return run;
  };

  const sendDurableCopilotMessage = async (store, messageAction) => {
    const runPayload = {
      message: messageAction.message,
      data_json: getCopilotMessageDataJsonForAction(messageAction)
    };

    try {
      const runResponse = await startCopilotRun(
        messageAction.chatUuid,
        runPayload
      );
      const runUuid = getCopilotRunUuid(runResponse);

      if (!runUuid) {
        throw new Error('Missing Copilot run UUID');
      }

      const run = {
        chatUuid: messageAction.chatUuid,
        runUuid,
        status: runResponse.status || 'pending',
        startedAt: new Date().toISOString()
      };

      dispatchOptimisticCopilotUserMessage(store, messageAction);
      return beginDurableCopilotRun(store, run);
    } catch (error) {
      const conflictRun =
        error?.name === HTTP_CONFLICT_ERROR_NAME || error?.status === 409
          ? camelize(error?.data || {})
          : null;
      const runUuid = getCopilotRunUuid(conflictRun);

      if (runUuid) {
        dispatchOptimisticCopilotUserMessage(store, messageAction);

        const run = {
          chatUuid: messageAction.chatUuid,
          runUuid,
          status: conflictRun.status || 'running',
          startedAt: new Date().toISOString()
        };
        return beginDurableCopilotRun(store, run);
      }

      logError('Error starting Copilot stream run', {
        chatUuid: messageAction.chatUuid,
        errorMessage: error?.message,
        status: error?.status,
        response: error?.data
      });
      store.dispatch({ type: CopilotActions.MESSAGE_TIMEOUT });
      throw error;
    }
  };

  const onOpen = store => event => {
    connecting = false;

    store.dispatch({
      type: WebsocketActions.CONNECTED,
      host: event.target.url
    });

    if (pending && socket && socket?.readyState === WebSocket.OPEN) {
      pending.forEach(action => {
        socket.send(getMessageForAction(store, action, 'sub'));
        store.dispatch({
          ...action,
          type: WebsocketActions.SUBSCRIBED
        });
      });

      pending = null;
    }

    if (
      pendingCopilotMessages.length > 0 &&
      socket?.readyState === WebSocket.OPEN
    ) {
      const messagesToProcess = [...pendingCopilotMessages];
      pendingCopilotMessages = [];

      messagesToProcess.forEach(messageAction => {
        try {
          socket.send(getMessageForAction(store, messageAction, 'send'));
        } catch (error) {
          logError('Error sending queued copilot message', error);
        }
      });
    }

    if (pendingOmnisearchAction && socket?.readyState === WebSocket.OPEN) {
      const omnisearchAction = pendingOmnisearchAction;
      pendingOmnisearchAction = null;
      try {
        socket.send(getOmnisearchSocketPayload(store, omnisearchAction));
      } catch (error) {
        logError('Error sending queued copilot omnisearch', error);
        store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
      }
    }
  };

  const onClose = store => () => {
    connecting = false;

    if (pendingOmnisearchAction) {
      pendingOmnisearchAction = null;
      store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
    }

    store.dispatch({ type: WebsocketActions.DISCONNECTED });

    socket = null;
  };

  const onMessage = store => event => {
    const payload = camelize(JSON.parse(event.data));

    switch (payload.stream) {
      case WebsocketActions.TYPE_BATTLECARDS:
        if (isEmpty(payload.data)) {
          logError(event.data, payload);
        } else {
          if (payload.data.dataType === 'answer_card') {
            store.dispatch({
              type: BattlecardActions.FETCH_BATTLECARD_SUCCESS,
              card: payload.data
            });

            // Track the event if the battlecard is triggered by the system
            store.dispatch(
              trackSegmentEvents({
                type: 'ANSWER_CARD_AUTO_DISPLAYED'
              })
            );
          }

          if (payload.data.dataType === 'rt_assistant') {
            store.dispatch({
              type: RealtimeAssistantActions.FETCH_REALTIME_ASSISTANT_SUCCESS,
              methodology: payload.data?.topicCovered
            });
          }
        }

        break;

      case WebsocketActions.TYPE_MEETING_REALTIME_TRANSCRIPTIONS:
        if (isEmpty(payload.data)) {
          // Filter out unnecessary messages from sentry logs
          // Do not log if the payload data is an empty object since that is not an error case
          if (payload.data === null || payload.data === undefined) {
            logError(event.data, payload);
          }
        } else if (payload.data.type === 'notes') {
          // Handle the translated notes here
          store.dispatch({
            type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_NOTES_SUCCESS,
            meetingUuid: payload.data.meetingUuid,
            data: payload.data
          });
        } else if (payload.data.type === 'transcript') {
          // Handle the translated transcript here
          store.dispatch({
            type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_TRANSCRIPT_SUCCESS,
            meetingUuid: payload.data.meetingUuid,
            data: payload.data
          });
        } else {
          // Handle real time transcript message here
          store.dispatch({
            type: RealtimeTranscriptFetchActions.FETCH_REALTIME_TRANSCRIPTION_SUCCESS,
            transcript: payload.data
          });
        }

        break;

      case WebsocketActions.TYPE_BOOKMARKS:
        if (isEmpty(payload.data)) {
          logError(event.data, payload);
        } else {
          store.dispatch({
            type: BookmarkActions.FETCH_BOOKMARK_SUCCESS,
            bookmark: payload.data
          });
        }

        break;

      case WebsocketActions.TYPE_MEETING_META:
        if (isEmpty(payload.dbObject)) {
          logError(event.data, payload);
        } else {
          if (payload.data?.scorecardUuid) {
            store.dispatch({
              type: ScorecardsActions.SET_SCORECARD_UUID_TO_FETCH,
              response: payload.data?.scorecardUuid
            });
          }

          if (payload.data?.recommendationUuid) {
            store.dispatch({
              type: CoachingActions.SET_COACHING_RECOMMENDATION_UUID_TO_FETCH,
              response: payload.data
            });
          }

          if (
            payload.data &&
            'title' in payload.data &&
            'summary' in payload.data
          ) {
            store.dispatch({
              type: HighlightActions.SET_HIGHLIGHT_SUMMARIZATION,
              response: payload.data
            });
          }

          store.dispatch({
            type: MeetingActions.FETCH_META_SUCCESS,
            meetingId: payload.dbObject.uuid,
            metadata: payload.data
          });

          store.dispatch({
            type: MeetingActions.SET_ONLINE_USERS,
            meetingUuid: payload.dbObject.uuid,
            onlineUsers: payload.data.subscribedUsers
          });
        }

        break;

      case WebsocketActions.TYPE_COPILOT: {
        const isOmnisearchResponse =
          payload.messageType === 'omnisearch' ||
          (isEmpty(payload.dbObject) &&
            payload.data &&
            (Array.isArray(payload.data.meeting) ||
              Array.isArray(payload.data.deal) ||
              Array.isArray(payload.data.user) ||
              Array.isArray(payload.data.team) ||
              Array.isArray(payload.data.playlist) ||
              Array.isArray(payload.data.company) ||
              Array.isArray(payload.data.account)));
        if (isOmnisearchResponse && payload.data) {
          const companyList =
            payload.data.company !== undefined
              ? payload.data.company
              : payload.data.account;
          store.dispatch({
            type: CopilotActions.RECEIVE_COPILOT_OMNISEARCH,
            data: {
              requestId: readCopilotOmnisearchRequestIdFromWsData(payload.data),
              meeting: payload.data?.meeting,
              deal: payload.data?.deal,
              user: payload.data?.user,
              team: payload.data?.team,
              playlist: payload.data?.playlist,
              company: companyList,
              /** Relevance order for grouped omnisearch sections (camelized from `context_type_order`). */
              contextTypeOrder: payload.data?.contextTypeOrder
            }
          });
          break;
        }
        if (isEmpty(payload.dbObject)) {
          logError(event.data, payload);
        } else {
          dispatchCopilotPayload(store, payload);
        }

        break;
      }

      default:
        break;
    }
  };

  const connect = store => {
    connecting = true;

    const host = env.websocketUrl;

    // connect to the remote host
    socket = new WebSocket(host);

    // websocket handlers
    socket.onmessage = onMessage(store);
    socket.onclose = onClose(store);
    socket.onopen = onOpen(store);
  };

  // the middleware part of this function
  return store => next => action => {
    switch (action.type) {
      case WebsocketActions.CONNECT: {
        if (socket !== null) {
          socket.close();
        }

        if (!connecting) {
          connect(store);
        }

        break;
      }

      case WebsocketActions.DISCONNECT:
        if (socket !== null) {
          socket.close();
        }

        socket = null;

        break;

      case WebsocketActions.SUBSCRIBE: {
        const message = getMessageForAction(store, action, 'sub');

        if (socket && socket?.readyState === WebSocket.OPEN) {
          socket.send(message);
          store.dispatch({
            ...action,
            type: WebsocketActions.SUBSCRIBED
          });
        } else {
          if (!connecting) {
            connect(store);
          }

          if (!pending) {
            pending = [];
          }

          pending.push(action);
        }

        break;
      }

      case WebsocketActions.UNSUBSCRIBE: {
        const message = getMessageForAction(store, action, 'unsub');

        if (socket && socket?.readyState === WebSocket.OPEN) {
          socket.send(message);

          store.dispatch({
            ...action,
            type: WebsocketActions.UNSUBSCRIBED
          });
        } else {
          if (!connecting) {
            connect(store);
          }

          if (pending && socket && socket?.readyState === WebSocket.OPEN) {
            // Removing if we have any pending `sub` for same meeting uuid as of `unsub`
            pending = pending.filter(
              pendingAction =>
                !(
                  action &&
                  pendingAction &&
                  action.streamType === pendingAction.streamType &&
                  action.uuid === pendingAction.uuid &&
                  action.entityKey === pendingAction.entityKey
                )
            );
          }
        }

        break;
      }

      case CopilotActions.SEND_COPILOT_MESSAGE: {
        if (isCopilotSseStreamEnabled(store)) {
          const durableRunPromise = sendDurableCopilotMessage(store, action);
          next(action);
          return durableRunPromise;
        }

        const processCopilotMessage = messageAction => {
          if (!socket || socket?.readyState !== WebSocket.OPEN) {
            pendingCopilotMessages.push(messageAction);
            return;
          }

          processingSendMessage = true;

          try {
            socket.send(getMessageForAction(store, messageAction, 'send'));
          } catch (error) {
            logError('Error sending receiving message from copilot', error);
          } finally {
            processingSendMessage = false;

            if (pendingCopilotMessages.length > 0) {
              const nextMessage = pendingCopilotMessages.shift();
              processCopilotMessage(nextMessage);
            }
          }
        };

        if (processingSendMessage) {
          pendingCopilotMessages.push(action);
        } else {
          processCopilotMessage(action);
        }
        break;
      }

      case CopilotActions.SEND_COPILOT_OMNISEARCH: {
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(getOmnisearchSocketPayload(store, action));
          } catch (error) {
            logError('Error sending copilot omnisearch', error);
            return next({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
          }
        } else {
          pendingOmnisearchAction = action;
          if (!connecting) {
            connect(store);
          }
        }
        return next(action);
      }

      case CopilotActions.CLEAR_COPILOT_OMNISEARCH: {
        pendingOmnisearchAction = null;
        return next(action);
      }

      case CopilotActions.RECONNECT_COPILOT_RUNS: {
        if (!isCopilotSseStreamEnabled(store)) {
          break;
        }

        const storedRuns = loadStoredCopilotRuns();
        store.dispatch({
          type: CopilotActions.REHYDRATE_COPILOT_RUNS,
          runs: storedRuns
        });
        Object.values(storedRuns).forEach(run => {
          if (run.status === 'pending' || run.status === 'running') {
            connectDurableCopilotRun(store, run);
          }
        });
        break;
      }

      case CopilotActions.DISCONNECT_COPILOT_SSE_RUN_FOR_CHAT: {
        if (!isCopilotSseStreamEnabled(store) || !action.chatUuid) {
          break;
        }

        pauseDurableCopilotSseForChat(store, action.chatUuid);
        break;
      }

      case CopilotActions.RECONNECT_COPILOT_SSE_RUN_FOR_CHAT: {
        if (!isCopilotSseStreamEnabled(store) || !action.chatUuid) {
          break;
        }

        const run =
          store.getState().copilot?.activeCopilotRunsByChatUuid?.[
            action.chatUuid
          ] || loadStoredCopilotRuns()[action.chatUuid];

        if (run && isActiveCopilotRunStatus(run.status)) {
          connectDurableCopilotRun(store, run);
        }
        break;
      }

      default:
        return next(action);
    }

    return next(action);
  };
};

export default socketMiddleware();
